import { useState, useEffect } from 'react';
import '@/App.css';
import '@/compare.css';
import axios from 'axios';
import { Upload, Trash2, FileSpreadsheet, BarChart3, Filter, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProjectsComparison() {
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('خطأ في جلب المشاريع:', error);
      toast.error('فشل في جلب المشاريع');
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setLoading(true);
    for (let file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(`تم رفع ${file.name} بنجاح`);
      } catch (error) {
        console.error('خطأ في رفع الملف:', error);
        toast.error(`فشل في رفع ${file.name}`);
      }
    }
    await fetchProjects();
    setLoading(false);
    event.target.value = '';
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('تم حذف المشروع بنجاح');
      await fetchProjects();
      setSelectedProjects(selectedProjects.filter(id => id !== projectId));
    } catch (error) {
      console.error('خطأ في حذف المشروع:', error);
      toast.error('فشل في حذف المشروع');
    }
  };

  const handleProjectSelection = (projectId) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter(id => id !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };

  const handleCompare = async () => {
    if (selectedProjects.length < 2) {
      toast.error('يرجى اختيار مشروعين على الأقل للمقارنة');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/compare`, { project_ids: selectedProjects, category_filter: categoryFilter || null });
      setComparisonData(response.data);
      setCategories(response.data.categories);
      toast.success('تمت المقارنة بنجاح');
    } catch (error) {
      console.error('خطأ في المقارنة:', error);
      toast.error('فشل في إجراء المقارنة');
    }
    setLoading(false);
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.post(`${API}/export/excel`, { project_ids: selectedProjects, category_filter: categoryFilter || null }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'comparison.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('تم تصدير التقرير بنجاح');
    } catch (error) {
      console.error('خطأ في التصدير:', error);
      toast.error('فشل في تصدير التقرير');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.post(`${API}/export/pdf`, { project_ids: selectedProjects, category_filter: categoryFilter || null }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'comparison.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('تم تصدير التقرير بصيغة PDF بنجاح');
    } catch (error) {
      console.error('خطأ في التصدير:', error);
      toast.error('فشل في تصدير التقرير بصيغة PDF');
    }
  };

  const [analysisReport, setAnalysisReport] = useState(null);
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [duplicatesReport, setDuplicatesReport] = useState(null);
  const [detectingDuplicates, setDetectingDuplicates] = useState(false);

  const handleGenerateAnalysis = async () => {
    if (selectedProjects.length < 2) {
      toast.error('يرجى اختيار مشروعين على الأقل لإنشاء التقرير');
      return;
    }

    setGeneratingAnalysis(true);
    try {
      const response = await axios.post(`${API}/generate-analysis`, {
        project_ids: selectedProjects,
        category_filter: categoryFilter || null
      });
      
      setAnalysisReport(response.data.analysis);
      toast.success('تم إنشاء التقرير التحليلي بنجاح');
      
      // Scroll to analysis section
      setTimeout(() => {
        document.getElementById('analysis-report')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } catch (error) {
      console.error('خطأ في إنشاء التقرير:', error);
      toast.error('فشل في إنشاء التقرير التحليلي');
    }
    setGeneratingAnalysis(false);
  };

  const handleDetectDuplicates = async () => {
    if (selectedProjects.length < 1) {
      toast.error('يرجى اختيار مشروع واحد على الأقل');
      return;
    }

    setDetectingDuplicates(true);
    try {
      const response = await axios.post(`${API}/detect-duplicates`, {
        project_ids: selectedProjects,
        category_filter: categoryFilter || null
      });
      
      setDuplicatesReport(response.data.report);
      toast.success('تم اكتشاف الأصناف المتشابهة بنجاح');
      
      // Scroll to duplicates section
      setTimeout(() => {
        document.getElementById('duplicates-report')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } catch (error) {
      console.error('خطأ في كشف التكرارات:', error);
      toast.error('فشل في كشف الأصناف المتشابهة');
    }
    setDetectingDuplicates(false);
  };

  const handleExportDuplicates = async (format) => {
    if (!duplicatesReport) {
      toast.error('لا يوجد تقرير لتصديره');
      return;
    }

    try {
      const endpoint = format === 'excel' ? '/export-duplicates/excel' : '/export-duplicates/pdf';
      const response = await axios.post(`${API}${endpoint}`, {
        report_data: duplicatesReport
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `duplicates.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`تم تصدير تقرير الأصناف المتشابهة بصيغة ${format === 'excel' ? 'Excel' : 'PDF'} بنجاح`);
    } catch (error) {
      console.error('خطأ في التصدير:', error);
      toast.error('فشل في تصدير التقرير');
    }
  };

  const prepareChartData = () => {
    if (!comparisonData || !comparisonData.common_items) return [];
    return comparisonData.common_items.slice(0, 10).map(item => {
      const dataPoint = { name: item.name.substring(0, 20) };
      comparisonData.project_names.forEach(projectName => {
        const projectData = item.projects[projectName];
        if (projectData) {
          dataPoint[`${projectName} - الكمية`] = projectData.quantity;
        }
      });
      return dataPoint;
    });
  };

  return (
    <div className="app-container" dir="rtl">
      <div className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title"><BarChart3 className="inline-block ml-3" size={48} />تطبيق مقارنة المشاريع</h1>
          <p className="hero-subtitle">قارن بين مشاريعك المختلفة وتتبع الأسعار والكميات بسهولة</p>
        </div>
      </div>
      <div className="main-content">
        <Card className="upload-card" data-testid="upload-section">
          <CardHeader>
            <CardTitle className="card-title"><Upload className="inline-block ml-2" />رفع ملفات Excel</CardTitle>
            <CardDescription>قم برفع ملفات Excel للمشاريع (يمكنك رفع عدة ملفات)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="upload-area">
              <input data-testid="file-input" type="file" id="fileInput" accept=".xlsx,.xls" multiple onChange={handleFileUpload} className="file-input" />
              <label htmlFor="fileInput" className="upload-label">
                <FileSpreadsheet size={48} className="upload-icon" />
                <span className="upload-text">اضغط هنا لرفع الملفات أو اسحبها</span>
                <span className="upload-subtext">يدعم ملفات Excel (.xlsx, .xls)</span>
              </label>
            </div>
            <div className="templates-section">
              <p className="templates-title">أو حمّل نماذج Excel جاهزة:</p>
              <div className="templates-buttons">
                <a href="/نموذج_مشروع_فارغ.xlsx" download className="template-link">
                  <Download size={18} className="ml-1" />
                  نموذج فارغ
                </a>
                <a href="/مشروع_البناء_1.xlsx" download className="template-link">
                  <Download size={18} className="ml-1" />
                  مشروع نموذج 1
                </a>
                <a href="/مشروع_البناء_2.xlsx" download className="template-link">
                  <Download size={18} className="ml-1" />
                  مشروع نموذج 2
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
        {projects.length > 0 && (
          <Card className="projects-card" data-testid="projects-list">
            <CardHeader>
              <CardTitle className="card-title">المشاريع المرفوعة ({projects.length})</CardTitle>
              <CardDescription>اختر المشاريع التي تريد مقارنتها</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="projects-grid">
                {projects.map((project) => (
                  <div key={project.id} className={`project-card ${selectedProjects.includes(project.id) ? 'selected' : ''}`} onClick={() => handleProjectSelection(project.id)} data-testid={`project-card-${project.id}`}>
                    <div className="project-info">
                      <div className="project-checkbox"><input type="checkbox" checked={selectedProjects.includes(project.id)} onChange={() => {}} className="checkbox" /></div>
                      <div className="project-details">
                        <h3 className="project-name">{project.name}</h3>
                        <p className="project-items">{project.items.length} صنف</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteProject(project.id); }} className="delete-btn" data-testid={`delete-project-${project.id}`}><Trash2 size={18} /></Button>
                  </div>
                ))}
              </div>
              {selectedProjects.length > 0 && (
                <div className="actions-section">
                  <div className="filter-section">
                    <Filter className="inline-block ml-2" size={20} />
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="filter-select" data-testid="category-filter"><SelectValue placeholder="فلترة حسب التصنيف" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">كل التصنيفات</SelectItem>
                        {categories.map(category => (<SelectItem key={category} value={category}>{category}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="action-buttons">
                    <Button onClick={handleCompare} disabled={loading || selectedProjects.length < 2} className="compare-btn" data-testid="compare-button"><BarChart3 className="ml-2" size={20} />مقارنة المشاريع ({selectedProjects.length})</Button>
                    {comparisonData && (
                      <>
                        <Button
                          onClick={handleExportExcel}
                          variant="outline"
                          className="export-btn"
                          data-testid="export-excel-button"
                        >
                          <Download className="ml-2" size={20} />
                          تصدير Excel
                        </Button>
                        <Button
                          onClick={handleExportPDF}
                          variant="outline"
                          className="export-btn pdf-btn"
                          data-testid="export-pdf-button"
                        >
                          <Download className="ml-2" size={20} />
                          تصدير PDF
                        </Button>
                        <Button
                          onClick={handleGenerateAnalysis}
                          disabled={generatingAnalysis}
                          className="analysis-btn"
                          data-testid="generate-analysis-button"
                        >
                          <BarChart3 className="ml-2" size={20} />
                          {generatingAnalysis ? 'جاري إنشاء التقرير...' : 'إنشاء تقرير تحليلي'}
                        </Button>
                        <Button
                          onClick={handleDetectDuplicates}
                          disabled={detectingDuplicates}
                          className="duplicates-btn"
                          data-testid="detect-duplicates-button"
                        >
                          <Filter className="ml-2" size={20} />
                          {detectingDuplicates ? 'جاري الكشف...' : 'كشف الأصناف المتشابهة'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {comparisonData && (
          <div className="comparison-section" data-testid="comparison-results">
            <Card className="stats-card">
              <CardHeader><CardTitle className="card-title">إحصائيات المقارنة</CardTitle></CardHeader>
              <CardContent>
                <div className="stats-grid">
                  <div className="stat-item"><div className="stat-value">{comparisonData.stats.total_items}</div><div className="stat-label">إجمالي الأصناف</div></div>
                  <div className="stat-item"><div className="stat-value">{comparisonData.stats.common_items}</div><div className="stat-label">أصناف مشتركة</div></div>
                  <div className="stat-item"><div className="stat-value">{comparisonData.stats.partial_items}</div><div className="stat-label">أصناف غير مشتركة</div></div>
                </div>
              </CardContent>
            </Card>

            <Card className="costs-card">
              <CardHeader><CardTitle className="card-title">التكاليف الإجمالية</CardTitle></CardHeader>
              <CardContent>
                <div className="costs-comparison">
                  {comparisonData.project_names.map((projectName, idx) => {
                    const totalCost = comparisonData.costs?.total?.[projectName] || 0;
                    const commonCost = comparisonData.costs?.common?.[projectName] || 0;
                    const partialCost = comparisonData.costs?.partial?.[projectName] || 0;
                    
                    return (
                      <div key={idx} className="project-cost-card">
                        <h3 className="project-cost-title">{projectName}</h3>
                        <div className="cost-breakdown">
                          <div className="cost-row total">
                            <span className="cost-label">إجمالي التكلفة:</span>
                            <span className="cost-value">{totalCost.toLocaleString('ar-SA', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="cost-row common">
                            <span className="cost-label">تكلفة الأصناف المشتركة:</span>
                            <span className="cost-value">{commonCost.toLocaleString('ar-SA', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="cost-row partial">
                            <span className="cost-label">تكلفة الأصناف غير المشتركة:</span>
                            <span className="cost-value">{partialCost.toLocaleString('ar-SA', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {comparisonData.project_names.length === 2 && (
                  <div className="cost-difference">
                    <h4 className="diff-title">الفرق بين المشروعين</h4>
                    <div className="diff-details">
                      <div className="diff-row">
                        <span className="diff-label">فرق التكلفة الإجمالية:</span>
                        <span className="diff-amount">
                          {Math.abs(
                            (comparisonData.costs?.total?.[comparisonData.project_names[0]] || 0) - 
                            (comparisonData.costs?.total?.[comparisonData.project_names[1]] || 0)
                          ).toLocaleString('ar-SA', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                      <div className="diff-row">
                        <span className="diff-label">فرق تكلفة الأصناف المشتركة:</span>
                        <span className="diff-amount">
                          {Math.abs(
                            (comparisonData.costs?.common?.[comparisonData.project_names[0]] || 0) - 
                            (comparisonData.costs?.common?.[comparisonData.project_names[1]] || 0)
                          ).toLocaleString('ar-SA', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                      <div className="diff-row">
                        <span className="diff-label">فرق تكلفة الأصناف غير المشتركة:</span>
                        <span className="diff-amount">
                          {Math.abs(
                            (comparisonData.costs?.partial?.[comparisonData.project_names[0]] || 0) - 
                            (comparisonData.costs?.partial?.[comparisonData.project_names[1]] || 0)
                          ).toLocaleString('ar-SA', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="differences-card">
              <CardHeader><CardTitle className="card-title">أهم الفروقات</CardTitle></CardHeader>
              <CardContent>
                <div className="differences-grid">
                  {comparisonData.common_items.slice(0, 5).map((item, idx) => {
                    const projects = comparisonData.project_names.map(name => item.projects[name]);
                    const prices = projects.map(p => p?.price || 0).filter(p => p > 0);
                    if (prices.length < 2) return null;
                    const maxPrice = Math.max(...prices);
                    const minPrice = Math.min(...prices);
                    const priceDiff = maxPrice - minPrice;
                    const pricePercent = ((priceDiff / minPrice) * 100).toFixed(1);
                    
                    if (priceDiff === 0) return null;
                    
                    return (
                      <div key={idx} className="difference-item">
                        <div className="diff-item-header">
                          <span className="diff-code">{item.code}</span>
                          <span className="diff-name">{item.name}</span>
                        </div>
                        <div className="diff-values">
                          <div className="diff-value-item">
                            <span className="diff-label">أقل سعر:</span>
                            <span className="diff-amount min">{minPrice.toFixed(2)}</span>
                          </div>
                          <div className="diff-arrow">→</div>
                          <div className="diff-value-item">
                            <span className="diff-label">أعلى سعر:</span>
                            <span className="diff-amount max">{maxPrice.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="diff-summary">
                          فرق: <strong>{priceDiff.toFixed(2)}</strong> ({pricePercent}%)
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              </CardContent>
            </Card>

            {prepareChartData().length > 0 && (
              <Card className="chart-card">
                <CardHeader><CardTitle className="card-title">مقارنة الكميات (أول 10 أصناف)</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={prepareChartData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {comparisonData.project_names.map((name, idx) => (<Bar key={name} dataKey={`${name} - الكمية`} fill={['#4A90E2', '#50C878', '#FF6B6B', '#FFD93D'][idx % 4]} />))}
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            <Card className="items-card">
              <CardHeader><CardTitle className="card-title">الأصناف المشتركة ({comparisonData.common_items.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="table-container">
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        <th rowSpan={2}>الكود</th>
                        <th rowSpan={2}>اسم الصنف</th>
                        <th rowSpan={2}>التصنيف</th>
                        {comparisonData.project_names.map(name => (<th key={name} colSpan={2}>{name}</th>))}
                        <th colSpan={2}>الفرق</th>
                      </tr>
                      <tr>
                        {comparisonData.project_names.map(name => (<><th key={`${name}-qty`}>الكمية</th><th key={`${name}-price`}>السعر</th></>))}
                        <th>فرق الكمية</th>
                        <th>فرق السعر</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.common_items.map((item, idx) => {
                        const projects = comparisonData.project_names.map(name => item.projects[name]);
                        const quantities = projects.map(p => p?.quantity || 0);
                        const prices = projects.map(p => p?.price || 0);
                        const maxQty = Math.max(...quantities);
                        const minQty = Math.min(...quantities.filter(q => q > 0));
                        const maxPrice = Math.max(...prices);
                        const minPrice = Math.min(...prices.filter(p => p > 0));
                        const qtyDiff = maxQty - minQty;
                        const priceDiff = maxPrice - minPrice;
                        const pricePercent = minPrice > 0 ? ((priceDiff / minPrice) * 100).toFixed(1) : 0;
                        
                        return (
                          <tr key={idx}>
                            <td>{item.code}</td>
                            <td>{item.name}</td>
                            <td>{item.category}</td>
                            {comparisonData.project_names.map(name => { 
                              const projectData = item.projects[name];
                              const isMaxQty = projectData?.quantity === maxQty;
                              const isMinQty = projectData?.quantity === minQty && minQty > 0;
                              const isMaxPrice = projectData?.price === maxPrice;
                              const isMinPrice = projectData?.price === minPrice && minPrice > 0;
                              
                              return (
                                <>
                                  <td key={`${name}-qty`} className={isMaxQty ? 'highlight-max' : isMinQty ? 'highlight-min' : ''}>
                                    {projectData?.quantity || '-'}
                                  </td>
                                  <td key={`${name}-price`} className={isMaxPrice ? 'highlight-max' : isMinPrice ? 'highlight-min' : ''}>
                                    {projectData?.price || '-'}
                                  </td>
                                </>
                              ); 
                            })}
                            <td className="diff-cell">{qtyDiff > 0 ? qtyDiff.toFixed(2) : '-'}</td>
                            <td className="diff-cell">{priceDiff > 0 ? `${priceDiff.toFixed(2)} (${pricePercent}%)` : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            {comparisonData.partial_items.length > 0 && (
              <Card className="items-card">
                <CardHeader><CardTitle className="card-title">الأصناف غير المشتركة ({comparisonData.partial_items.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="table-container">
                    <table className="comparison-table">
                      <thead>
                        <tr>
                          <th>الكود</th><th>اسم الصنف</th><th>التصنيف</th>
                          {comparisonData.project_names.map(name => (<th key={name} colSpan={2}>{name}</th>))}
                        </tr>
                        <tr>
                          <th colSpan={3}></th>
                          {comparisonData.project_names.map(name => (<><th key={`${name}-qty`}>الكمية</th><th key={`${name}-price`}>السعر</th></>))}
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonData.partial_items.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.code}</td><td>{item.name}</td><td>{item.category}</td>
                            {comparisonData.project_names.map(name => { const projectData = item.projects[name]; return (<><td key={`${name}-qty`} className={!projectData ? 'empty-cell' : ''}>{projectData?.quantity || '-'}</td><td key={`${name}-price`} className={!projectData ? 'empty-cell' : ''}>{projectData?.price || '-'}</td></>); })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {analysisReport && (
              <Card className="analysis-report-card" id="analysis-report">
                <CardHeader>
                  <CardTitle className="card-title">التقرير التحليلي الشامل</CardTitle>
                  <CardDescription>تقرير تفصيلي مُنشأ بواسطة الذكاء الاصطناعي</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="analysis-content">
                    {analysisReport.split('\n').map((paragraph, idx) => {
                      if (paragraph.startsWith('###')) {
                        return <h3 key={idx} className="analysis-h3">{paragraph.replace(/###/g, '')}</h3>;
                      } else if (paragraph.startsWith('##')) {
                        return <h2 key={idx} className="analysis-h2">{paragraph.replace(/##/g, '')}</h2>;
                      } else if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                        return <h4 key={idx} className="analysis-h4">{paragraph.replace(/\*\*/g, '')}</h4>;
                      } else if (paragraph.startsWith('-')) {
                        return <li key={idx} className="analysis-li">{paragraph.substring(1).trim()}</li>;
                      } else if (paragraph.trim()) {
                        return <p key={idx} className="analysis-p">{paragraph}</p>;
                      }
                      return null;
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {duplicatesReport && (
              <Card className="duplicates-report-card" id="duplicates-report">
                <CardHeader>
                  <CardTitle className="card-title">تقرير كشف الأصناف المتشابهة</CardTitle>
                  <CardDescription>أصناف محتمل أن تكون متكررة بأكواد مختلفة - مُنشأ بالذكاء الاصطناعي</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="duplicates-summary">
                    <div className="summary-item">
                      <span className="summary-label">عدد الحالات المشتبهة:</span>
                      <span className="summary-value">{duplicatesReport.summary?.total_suspicious_cases || 0}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">نسبة التكرار:</span>
                      <span className="summary-value">{duplicatesReport.summary?.duplicate_percentage || 0}%</span>
                    </div>
                  </div>

                  <div className="export-buttons-section">
                    <Button onClick={() => handleExportDuplicates('excel')} variant="outline" className="export-btn">
                      <Download className="ml-2" size={18} />
                      تصدير Excel
                    </Button>
                    <Button onClick={() => handleExportDuplicates('pdf')} variant="outline" className="export-btn pdf-btn">
                      <Download className="ml-2" size={18} />
                      تصدير PDF
                    </Button>
                  </div>

                  <div className="table-container">
                    <table className="duplicates-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>الأكواد</th>
                          <th>الأسماء</th>
                          <th>التصنيف</th>
                          <th>سبب الشك</th>
                        </tr>
                      </thead>
                      <tbody>
                        {duplicatesReport.duplicates?.map((dup, idx) => (
                          <tr key={idx}>
                            <td>{dup.group_id}</td>
                            <td className="codes-cell">{dup.codes.join(', ')}</td>
                            <td className="names-cell">{dup.names.join(' | ')}</td>
                            <td>{dup.category}</td>
                            <td className="reason-cell">{dup.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {duplicatesReport.patterns && duplicatesReport.patterns.length > 0 && (
                    <div className="patterns-section">
                      <h3 className="section-title">الأنماط المكتشفة:</h3>
                      <ul className="patterns-list">
                        {duplicatesReport.patterns.map((pattern, idx) => (
                          <li key={idx}>{pattern}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {duplicatesReport.recommendations && duplicatesReport.recommendations.length > 0 && (
                    <div className="recommendations-section">
                      <h3 className="section-title">التوصيات:</h3>
                      <ul className="recommendations-list">
                        {duplicatesReport.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectsComparison;